import { DatePipe } from '@angular/common';
import {
    Component,
    EventEmitter,
    Output
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import { IMaskDirective } from 'angular-imask';
import { FactoryArg } from 'imask/masked/factory';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Nullable } from 'primeng/ts-helpers';

import { AuthService } from '@/auth/services';
import {
    AppLoaderComponent,
    AppRoleComponent,
    AppStatusComponent,
    VerifiableInput
} from '@/components';
import { AvatarComponent } from '@/components/avatar/avatar.component';
import { User } from '@/interfaces';
import { ValueOrPipe } from '@/pipes';
import { ValidationService } from '@/service/validation.service';

@Component({
    selector: 'app-profile',
    templateUrl: 'profile.component.html',
    imports: [
        AvatarComponent,
        AppStatusComponent,
        AppRoleComponent,
        ValueOrPipe,
        DatePipe,
        Button,
        InputText,
        ReactiveFormsModule,
        VerifiableInput,
        IMaskDirective,
        AppLoaderComponent
    ],
    standalone: true
})
export class ProfileComponent {

    @Output() onClose = new EventEmitter<void>();

    user: Nullable<User>;
    editMode = false;
    form!: FormGroup;
    mask: FactoryArg = { mask: '(000) 000-0000' };
    loading = false;

    constructor(
        public authService: AuthService,
        private fb: FormBuilder,
        private validationService: ValidationService,
        private messageService: MessageService
    ) {
        this.user = this.authService.user;
        this.form = this.fb.group({
            name: [null, Validators.required],
            phone: null
        });
    }

    toggleEdit(): void {
        this.editMode = !this.editMode;
        if (this.editMode && this.user) {
            this.form.patchValue(this.user);
        } else {
            this.form.reset();
        }
    }

    updateProfile(): void {
        this.validationService.clearAllErrors();
        const payload = this.form.getRawValue();
        if (!payload.name) {
            this.validationService.setError('update-profile', 'name', 'Name is required.');
        } else if (payload.name.length > 100) {
            this.validationService.setError('update-profile', 'name', 'Name must be at most 100 characters.');
        }
        if (!this.validationService.checkValidity('update-profile')) {
            return;
        }
        this.loading = true;
        this.authService.profile(payload).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    detail: 'Profile updated successfully.'
                });
                this.editMode = false;
                this.onClose.emit();
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }
}
